package com.essntl.uikit.components.chat.conversation.overviewcard

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import core.dateformat.utils.formatTimestampToYYYYMMDDOrToHHMM
import com.essntl.uikit.components.chat.contact.ContactAvatar
import com.essntl.uikit.components.chat.contact.ContactTypeUI
import com.essntl.uikit.components.chat.group.GroupAvatar
import com.essntl.uikit.components.chat.message.MessageUiState
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.icons.EsMediumIcon
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun EsConversationOverviewCard(
    uiState: ConversationOverviewUiState,
    isSelected: Boolean,
    onClick: (String, ConversationOverviewUiState.ChatType) -> Unit,
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius))
            .background(
                if (isSelected)
                    MaterialTheme.esColorScheme.bgPrimary
                else
                    Color.White,
            )
            .border(
                width = 1.dp,
                color = MaterialTheme.esColorScheme.borderPrimary,
                shape = RoundedCornerShape(EsConstants.SectionBorderRadius),
            )
            .clickable(
                enabled = enabled,
            ) {
                onClick(uiState.conversationId, uiState.type)
            }
            .padding(12.dp),
    ) {

        if (uiState.type == ConversationOverviewUiState.ChatType.Contact)
            ContactAvatar(
                initials = uiState.contact.initials,
                isOnline = uiState.contact.isOnline,
            )
        else if (uiState.type == ConversationOverviewUiState.ChatType.Group)
            GroupAvatar()

        Spacer(modifier = Modifier.width(8.dp))

        Column {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(
                    text = uiState.contact.name ?: uiState.contact.phoneNumber ?: "Unknown",
                    style = MaterialTheme.esTypography.p2Medium14.copy(
                        color = MaterialTheme.esColorScheme.textPrimary,
                        fontWeight = if (uiState.unreadMessages > 0)
                            FontWeight.Bold
                        else
                            FontWeight.Normal,
                    ),
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1F),
                )

                Text(
                    text = formatTimestampToYYYYMMDDOrToHHMM(uiState.dateTime),
                    color = if (uiState.unreadMessages > 0)
                        MaterialTheme.esColorScheme.textPrimary
                    else
                        MaterialTheme.esColorScheme.textDisabled,
                    style = MaterialTheme.esTypography.c1Medium11.copy(
                        fontWeight = if (uiState.unreadMessages > 0)
                            FontWeight.Bold
                        else
                            FontWeight.Normal,
                    ),
                    textAlign = TextAlign.End,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1F),
                )
            }

            Spacer(modifier = Modifier.height(4.dp))

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
                modifier = Modifier.fillMaxWidth(),
            ) {
                if (uiState.isCurrentUserSender) {
                    EsMediumIcon(
                        res = if (uiState.lastMessageStatus == MessageUiState.Status.Pending) EsIcons.Tick else
                            EsIcons.DoubleTick,
                        tint = if (uiState.lastMessageStatus == MessageUiState.Status.Read) Color.Green else
                            MaterialTheme.esColorScheme.iconDisabled,
                    )

                    Spacer(modifier = Modifier.width(.5.spacing))
                }

                // Last Message
                Text(
                    text = uiState.lastMessage,
                    style = MaterialTheme.esTypography.p2Regular14.copy(
                        color = if (uiState.unreadMessages > 0)
                            MaterialTheme.esColorScheme.textPrimary
                        else
                            MaterialTheme.esColorScheme.textSecondary,
                        fontWeight = if (uiState.unreadMessages > 0)
                            FontWeight.Bold
                        else
                            FontWeight.Normal,
                    ),
                    overflow = TextOverflow.Ellipsis,
                    maxLines = 1,
                    modifier = Modifier
                        .fillMaxWidth(if (uiState.unreadMessages > 0) .9F else 1F),
                )

                // Unread Count Badge
                if (uiState.unreadMessages > 0)
                    Box(
                        modifier = Modifier
                            .size(20.dp)
                            .clip(CircleShape)
                            .background(MaterialTheme.esColorScheme.bgContrast)
                            .fillMaxWidth(.1F),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            text = if (uiState.unreadMessages > 9)
                                "9+"
                            else
                                uiState.unreadMessages.toString(),
                            style = MaterialTheme.esTypography.c1Medium11.copy(
                                color = Color.White,
                            ),
                        )
                    }
            }

            Spacer(modifier = Modifier.height(4.dp))

            Row(
                verticalAlignment = Alignment.CenterVertically,
            ) {
                // Conversation Type
                EsConversationTypeUi(
                    conversationType = uiState.conversationType,
                    modifier = Modifier.weight(1F),
                )

                if (uiState.contact.type != null)
                    ContactTypeUI(uiState.contact.type)
            }
        }
    }
}